// Cache Design System — supports light + dark themes
// Direction: editorial-modern (NYT Cooking, Linear) — NOT financial-terminal
// Fonts: DM Sans (body + headings) + IBM Plex Mono (numerics) via expo-google-fonts
//
// Both palettes are defined as parallel exports (`darkColors`, `lightColors`,
// `darkTheme`, `lightTheme`). The runtime switcher lives in
// src/styles/ThemeProvider.tsx — consumers call useTheme() / useCommonStyles()
// to get the active palette. The static `colors` / `theme` / default exports
// remain as backwards-compat shims aliased to dark; they will be removed once
// every consumer is migrated to hooks (Phase B-2).

// ───────────────────────────────────────────────────────────────────
// DARK PALETTE — current default. Wired into every consumer today.
// ───────────────────────────────────────────────────────────────────
export const darkColors = {
  backgroundPrimary: '#0D0D0D',
  backgroundSurface: '#1A1A1A',
  backgroundElevated: '#222222',

  textPrimary: '#F2EDE4',
  textSecondary: '#8C8680',
  textTertiary: '#5C5854',

  accent: '#4A7C72',
  accentPressed: '#3A6560',
  accentSecondary: '#8FBDB6',
  accentSecondaryPressed: '#7AADA6',
  accentDisabled: '#2E4E4A',
  destructive: '#8B2020',

  border: '#2A2A2A',

  // Trend colours for charts and price deltas. Decoupled from the teal
  // accent so positive/negative reads at a glance. Green / orange instead
  // of green / red — orange is less alarmist for routine market dips and
  // leaves real red free for `destructive` (delete buttons, etc.).
  // Slightly brighter than the light variants since dark backgrounds
  // mute saturated colours.
  trendUp:   '#6FBE7F',
  trendDown: '#E89855',

  condition: {
    mint:      '#4A7C72',
    excellent: '#5A8F6E',
    good:      '#7A8F5A',
    fair:      '#8F7A3A',
    poor:      '#8F4A3A',
    forParts:  '#5C5854',
  },
} as const;

// ───────────────────────────────────────────────────────────────────
// LIGHT PALETTE — Phase A new addition. Locked 2026-05-06.
// Direction: warm editorial. Off-white pages, pure-white cards lift
// above the page, restrained teal accent.
// ───────────────────────────────────────────────────────────────────
export const lightColors = {
  // Page background — slightly cooler off-white with a hint of grey
  // undertone (less yellow than `#FBFAF6`). Reads as editorial-modern
  // rather than parchment-warm. White cards still lift above it.
  backgroundPrimary: '#F1EEE6',
  // Card surfaces — pure white sits ABOVE the off-white page. Free
  // elevation hierarchy without needing shadows everywhere.
  backgroundSurface: '#FFFFFF',
  // Modals — same as surface, separated by shadow rather than tone.
  backgroundElevated: '#FFFFFF',

  // Deep warm near-black, NOT pure black. Feels less harsh than `#000`.
  textPrimary: '#1A1410',
  textSecondary: '#5C544C',
  // Lower contrast than secondary, as it should be for "low priority"
  // (timestamps, captions, hints). Note this is the SAME hex as dark
  // theme's textSecondary (`#8C8680`), not its textTertiary — grey on
  // white needs less ink than grey on dark for the same perceived
  // hierarchy.
  textTertiary: '#8C8680',

  // Slightly darker teal than dark mode (`#4A7C72`). Bumps body-text
  // contrast on white from ~4.4:1 (borderline) to ~5.2:1 (comfortable).
  accent: '#3D6862',
  accentPressed: '#2D544F',
  accentSecondary: '#6FA59C',
  accentSecondaryPressed: '#5C928A',
  accentDisabled: '#B5C9C5',

  // Same severity in both themes — destructive should still feel
  // destructive even in friendly light mode.
  destructive: '#8B2020',

  // Warm light grey — not pure `#E5E5E5`. Subtle warmth keeps the
  // page feeling editorial rather than clinical.
  border: '#E8E4DC',

  // Trend colours. Green up / orange down, decoupled from the teal accent
  // so charts read at a glance. Orange (not red) for "down" leaves red
  // free for actually destructive UI (delete buttons), and reads as
  // informational rather than alarming on routine 2% portfolio dips.
  trendUp:   '#5BA86C',
  trendDown: '#D88542',

  // Condition palette — five of six values are identical to dark theme.
  // The exception is `forParts`, which mirrors `textTertiary` in both
  // themes (the semantic is "lowest-value, neutral grey"); since
  // textTertiary itself differs between themes, forParts does too.
  // Greens / amber / brick all work on both backgrounds. The badge
  // bg-tint opacity will need to drop from 8% (dark) to ~5% (light)
  // since the tint is more visible on a white background; that's a
  // Phase B tweak in ConditionBadge, not a token change here.
  condition: {
    mint:      '#4A7C72',
    excellent: '#5A8F6E',
    good:      '#7A8F5A',
    fair:      '#8F7A3A',
    poor:      '#8F4A3A',
    forParts:  '#8C8680',
  },
} as const;

export const fontFamily = {
  body: 'fontBody',
  bodyMedium: 'fontBodyMedium',
  // IBM Plex Mono — used for any numeric display so prices/values column
  // up neatly. Reserve for numbers; body copy stays on DM Sans.
  mono: 'fontMono',
} as const;

export const typography = {
  h1: { fontFamily: fontFamily.bodyMedium, fontSize: 48, lineHeight: 54, letterSpacing: 0.5 },
  h2: { fontFamily: fontFamily.bodyMedium, fontSize: 36, lineHeight: 42, letterSpacing: 0.3 },
  h3: { fontFamily: fontFamily.bodyMedium, fontSize: 28, lineHeight: 34, letterSpacing: 0.2 },
  h4: { fontFamily: fontFamily.bodyMedium, fontSize: 22, lineHeight: 28, letterSpacing: 0.1 },

  lg: { fontFamily: fontFamily.body, fontSize: 17, lineHeight: 26 },
  md: { fontFamily: fontFamily.body, fontSize: 15, lineHeight: 23 },
  sm: { fontFamily: fontFamily.body, fontSize: 13, lineHeight: 20 },
  xs: { fontFamily: fontFamily.body, fontSize: 11, lineHeight: 16, letterSpacing: 0.6 },

  // Numeric displays use IBM Plex Mono — fixed-width digits keep prices
  // and totals aligned column-to-column. Letter-spacing is zeroed because
  // mono is already evenly tracked; the negatives we used for serif
  // displays would over-tighten here.
  valueLg: { fontFamily: fontFamily.mono, fontSize: 56, lineHeight: 60 },
  valueMd: { fontFamily: fontFamily.mono, fontSize: 36, lineHeight: 40 },
  valueSm: { fontFamily: fontFamily.mono, fontSize: 24, lineHeight: 28 },
} as const;

export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
  huge: 64,
} as const;

export const radius = {
  radiusSm: 4,
  radiusMd: 6,
  radiusLg: 8,
} as const;

export const borders = {
  borderWidth: 1,
  borderColor: darkColors.border,
} as const;

export const shadows = {
  cardShadow: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.4,
    shadowRadius: 4,
    elevation: 3,
  },
} as const;

// ───────────────────────────────────────────────────────────────────
// Theme objects — both palettes wrapped with shared typography/spacing/etc.
// Consumers read the active theme via useTheme() (src/styles/ThemeProvider.tsx).
// ───────────────────────────────────────────────────────────────────
export const darkTheme = {
  mode: 'dark' as const,
  colors: darkColors,
  fontFamily,
  typography,
  spacing,
  radius,
  borders,
  shadows,
} as const;

export const lightTheme = {
  mode: 'light' as const,
  colors: lightColors,
  fontFamily,
  typography,
  spacing,
  radius: radius,
  borders: {
    borderWidth: 1,
    borderColor: lightColors.border,
  },
  shadows: {
    // Slightly softer shadow on light. The dark-theme shadow is at 0.4
    // opacity which is invisible against `#0D0D0D`; on white at 0.4 it
    // would feel heavy.
    cardShadow: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 8,
      elevation: 2,
    },
  },
} as const;

// Theme is the union of both palettes' inferred types. With `as const`,
// each palette has literal-typed properties (e.g. `borderColor: '#2A2A2A'`
// for dark, `borderColor: '#E8E4DC'` for light). A union widens member
// access to `'#2A2A2A' | '#E8E4DC'`, which is assignable to RN's
// `string` style props. Picking just one (e.g. `typeof darkTheme`) would
// fail typecheck the moment a function tried to accept either palette,
// which useTheme() does.
export type Theme = typeof darkTheme | typeof lightTheme;
export type ThemeMode = 'light' | 'dark';

/**
 * Resolve a condition string to its palette colour. `themeArg` should come
 * from useTheme() — the active palette determines whether `forParts` /
 * `textTertiary` resolve to light or dark values.
 */
export function getConditionColor(condition: string, themeArg: Theme): string {
  const colors = themeArg.colors;
  const map: Record<string, string> = {
    'mint':      colors.condition.mint,
    'new':       colors.condition.mint,
    'excellent': colors.condition.excellent,
    'good':      colors.condition.good,
    'fair':      colors.condition.fair,
    'poor':      colors.condition.poor,
    'for parts': colors.condition.forParts,
    'parts':     colors.condition.forParts,
  };
  return map[condition.toLowerCase()] ?? colors.textTertiary;
}
